import { AppShell, OfflineBanner, TABS, Wordmark } from '@roots/ui';
import { useQuery } from '@tanstack/react-query';
// Routes — the frozen 5-tab IA (UI/UX §8.1) plus pre-auth and deep-link targets (App Flow §7.1).
import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  useNavigate,
  useRouterState,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { api } from './lib/api.js';
import { useAuth } from './lib/auth.js';
import { syncPush } from './lib/push.js';
import { AuthScreen } from './screens/auth.js';
import { ContributionsScreen } from './screens/contributions.js';
import { FamiliesScreen } from './screens/families.js';
import { GalleryScreen } from './screens/gallery.js';
import { HomeScreen } from './screens/home.js';
import { JoinScreen } from './screens/join.js';
import { MyMonthScreen } from './screens/my-month.js';
import { NewsletterScreen } from './screens/newsletter.js';
import { NotificationsScreen } from './screens/notifications.js';
import { ProfileScreen } from './screens/profile.js';

function Shell() {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  const { data: home } = useQuery({
    queryKey: ['home'],
    queryFn: () => api.home(),
    enabled: Boolean(user),
  });

  // Keep this device's push registration attached to whoever is signed in (no prompt).
  const userId = user?.id;
  useEffect(() => {
    if (userId) void syncPush().catch(() => undefined);
  }, [userId]);

  if (loading && !user) {
    return (
      <div className="flex min-h-dvh items-center justify-center bg-[var(--color-bg-primary)]">
        <Wordmark className="h-14 w-auto text-[var(--color-text-primary)]" />
      </div>
    );
  }

  // Pre-auth screens render without the shell (App Flow §1).
  if (!user) {
    if (pathname.startsWith('/join/')) return <Outlet />;
    return <AuthScreen />;
  }

  // New accounts finish their profile before anything else (App Flow §1): email-code
  // sign-ups arrive nameless, Google sign-ups land on /welcome with Google's name filled in.
  if (!user.name.trim() || pathname === '/welcome') return <AuthScreen initialStep="profile" />;

  const unanswered = (home?.families ?? []).reduce((sum, f) => sum + f.unanswered_prompt_count, 0);
  const hasNewEdition = home?.latest_edition?.is_new ?? false;

  const tabs = TABS.map((tab) => {
    if (tab.key === 'home' && home?.unread_notifications) {
      return { ...tab, badge: home.unread_notifications };
    }
    if (tab.key === 'my-month' && unanswered > 0) return { ...tab, badge: unanswered };
    if (tab.key === 'newsletter' && hasNewEdition) return { ...tab, badge: 'dot' as const };
    return tab;
  });

  const activeKey = tabs.find((t) => t.href !== '/' && pathname.startsWith(t.href))?.key ?? 'home';

  return (
    <AppShell
      tabs={tabs}
      activeKey={activeKey}
      onNavigate={(href) => void navigate({ to: href })}
      header={<OfflineBanner />}
    >
      <Outlet />
    </AppShell>
  );
}

const rootRoute = createRootRoute({ component: Shell });

const routes = [
  createRoute({ getParentRoute: () => rootRoute, path: '/', component: HomeScreen }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/notifications',
    component: NotificationsScreen,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/my-month', component: MyMonthScreen }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/newsletter',
    component: NewsletterScreen,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    // §5.2 — list and reader share one screen so ≥768 shows both columns.
    path: '/newsletter/$editionId',
    component: NewsletterScreen,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/families', component: FamiliesScreen }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/families/$familyId',
    component: FamiliesScreen,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/profile', component: ProfileScreen }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/profile/contributions',
    component: ContributionsScreen,
  }),
  createRoute({ getParentRoute: () => rootRoute, path: '/join/$token', component: JoinScreen }),
  // Dev-only design-system review surface (ARN-27).
  ...(import.meta.env.DEV
    ? [createRoute({ getParentRoute: () => rootRoute, path: '/gallery', component: GalleryScreen })]
    : []),
  createRoute({ getParentRoute: () => rootRoute, path: '/auth', component: AuthScreen }),
  createRoute({ getParentRoute: () => rootRoute, path: '/welcome', component: HomeScreen }),
];

const routeTree = rootRoute.addChildren(routes);

export const router = createRouter({ routeTree, defaultPreload: 'intent' });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
